import { useCallback, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { coverageHoursPairing } from 'graphql-queries';

import {
  CoverageHoursPair,
  WireRouting,
  toCoverageHoursPair,
} from './coverageHoursPairMapper';

export class CoverageHoursPairLoadError extends Error {
  constructor(message: string, options?: { cause?: unknown }) {
    super(message, options);
    this.name = 'CoverageHoursPairLoadError';
  }
}

export type CoverageHoursPairTarget = {
  category: string;
  issue: string | null;
};

type CoverageHoursPairingResponse = {
  coverageHoursPairing: WireRouting | null;
};

export type UseCoverageHoursPairResult = {
  pair: CoverageHoursPair | null;
  loading: boolean;
  error?: CoverageHoursPairLoadError;
  loadPair: (target: CoverageHoursPairTarget) => Promise<void>;
  clearPair: () => void;
};

export function useCoverageHoursPair(
  channelId: string
): UseCoverageHoursPairResult {
  const [pair, setPair] = useState<CoverageHoursPair | null>(null);
  const [error, setError] = useState<CoverageHoursPairLoadError | undefined>();

  const [executeQuery, { loading }] =
    useLazyQuery<CoverageHoursPairingResponse>(coverageHoursPairing, {
      fetchPolicy: 'network-only',
    });

  const clearPair = useCallback(() => {
    setPair(null);
    setError(undefined);
  }, []);

  const reportFailure = useCallback((cause?: unknown) => {
    setPair(null);
    setError(
      new CoverageHoursPairLoadError('Could not load coverage hours', { cause })
    );
  }, []);

  const loadPair = useCallback(
    async (target: CoverageHoursPairTarget) => {
      setError(undefined);

      let result;

      try {
        result = await executeQuery({
          variables: {
            channelId,
            category: target.category,
            issue: target.issue,
          },
        });
      } catch (cause) {
        reportFailure(cause);

        return;
      }

      if (result.error) {
        reportFailure(result.error);

        return;
      }

      if (!result.data) {
        reportFailure();

        return;
      }

      const nextPair = toCoverageHoursPair(result.data.coverageHoursPairing);

      if (!nextPair) {
        reportFailure();

        return;
      }

      setPair(nextPair);
    },
    [channelId, executeQuery, reportFailure]
  );

  return { pair, loading, error, loadPair, clearPair };
}
